import { useState, useMemo, useCallback, useRef } from "react";
import { useTourManager } from '../hooks/useTourManager';
import { TourEngine } from './onboarding/TourEngine';
import { VaultConfirmModal } from "./vault/VaultConfirmModal";
import {
  X,
  TrendingUp,
  ArrowRight,
  Calendar,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { formatCurrency } from "../lib/calculations";
import { useToast } from "./ui/ToastModal";
import {
  type PaymentLineItem,
  type PaymentAction,
  buildPaymentReconciliationInput,
  mergePaymentLineItemsByType,
  savePaymentReconciliation,
  pushToNextMonth,
  getActionColor,
} from "../lib/paymentLineItems";
import { logActivity } from "../lib/activity-logger";
import {
  type ClaimsReportRow,
  type SubcategoryClaimInfo,
  type ProgressLineItem,
  MONTH_SHORT,
  getNextMonth,
} from "../lib/paymentPanelShared.types";
import { ActionDropdown } from "../lib/paymentPanelShared";
import {
  calculateDaysBeforeDue,
  stableStringify,
} from "../lib/paymentVersions";
import { logError } from "../lib/logError";

// ─── Props ────────────────────────────────────────────────────
interface ProgressPaymentPanelProps {
  report: ClaimsReportRow;
  subcategoryClaims: SubcategoryClaimInfo[];
  existingLineItems: PaymentLineItem[];
  onClose: () => void;
  onSaved: () => void;
}

// ─── Component ────────────────────────────────────────────────
export function ProgressPaymentPanel({
  report,
  subcategoryClaims,
  existingLineItems,
  onClose,
  onSaved,
}: ProgressPaymentPanelProps) {
  const { addToast } = useToast();
  const tour = useTourManager('progress_payment_panel');
  const [saving, setSaving] = useState(false);

  // Base amounts: these are the reference for the panel
  const claimedToDate = Number(report.claimed_to_date ?? 0);
  const period = `${MONTH_SHORT[report.claim_month - 1]} ${report.claim_year}`;
  const nextPeriod = getNextMonth(report.claim_month, report.claim_year);
  const nextPeriodLabel = `${MONTH_SHORT[nextPeriod.month - 1]} ${nextPeriod.year}`;

  // ─── Progress Line Items (Subcategory-driven) ──────────────
  // Legacy support: if no line items exist but report has paid_by_builder,
  // distribute the existing progress_payment_pct across all subcategories
  const hasExistingLineItems = existingLineItems.some(li => li.line_type === "progress");
  const legacyPaidPct = Number(report.progress_payment_pct ?? 0);
  const legacyPaidAmount = Number(report.paid_by_builder ?? 0);
  const hasLegacyData = !hasExistingLineItems && (legacyPaidPct > 0 || legacyPaidAmount > 0);

  const [progressLines, setProgressLines] = useState<ProgressLineItem[]>(
    () => {
      if (subcategoryClaims.length === 0) return [];
      return subcategoryClaims.map((sc) => {
        // 1. First check for existing line items (new system)
        const existing = existingLineItems.find(
          (li) =>
            li.line_type === "progress" &&
            li.subcategory_id === sc.subcategory_id
        );
        if (existing) {
          return {
            subcategory_id: sc.subcategory_id,
            subcategory_name: sc.subcategory_name,
            category_name: sc.category_name,
            claimed_percentage: sc.claimed_percentage,
            claimed_amount: sc.claimed_amount,
            budget: sc.budget,
            paid_percentage: existing.paid_percentage,
            paid_amount: existing.paid_amount,
            action: existing.action,
            agreed_amount: existing.agreed_amount ?? null,
            notes: existing.notes ?? "",
          };
        }

        // 2. Legacy backfill: report has paid_by_builder but no line items
        //    Distribute the legacy percentage across all claimed subcategories
        if (hasLegacyData && sc.claimed_percentage > 0) {
          // If paid was 100%, each subcategory is fully paid
          // If paid was e.g. 87.6%, apply that ratio to each subcategory
          const ratio = Math.min(legacyPaidPct / 100, 1);
          const paidPct = Math.round(sc.claimed_percentage * ratio * 100) / 100;
          const paidAmt = Math.round(sc.claimed_amount * ratio * 100) / 100;
          return {
            subcategory_id: sc.subcategory_id,
            subcategory_name: sc.subcategory_name,
            category_name: sc.category_name,
            claimed_percentage: sc.claimed_percentage,
            claimed_amount: sc.claimed_amount,
            budget: sc.budget,
            paid_percentage: paidPct,
            paid_amount: paidAmt,
            action: "paid" as PaymentAction,
            agreed_amount: null,
            notes: "",
          };
        }

        // 3. Default: not yet actioned (new claim, no legacy data)
        return {
          subcategory_id: sc.subcategory_id,
          subcategory_name: sc.subcategory_name,
          category_name: sc.category_name,
          claimed_percentage: sc.claimed_percentage,
          claimed_amount: sc.claimed_amount,
          budget: sc.budget,
          paid_percentage: 0,
          paid_amount: 0,
          action: "pending" as PaymentAction,
          agreed_amount: null,
          notes: "",
        };
      });
    }
  );

  const [datePaid, setDatePaid] = useState(report.date_paid ?? "");

  // ─── Computed Summaries (read-only, derived from subcategories) ─
  const activeProgressLines = useMemo(
    () => progressLines.filter((pl) => pl.claimed_percentage > 0),
    [progressLines]
  );

  const totalClaimedAmount = useMemo(
    () => activeProgressLines.reduce((sum, pl) => sum + pl.claimed_amount, 0),
    [activeProgressLines]
  );

  const totalPaidAmount = useMemo(
    () =>
      activeProgressLines.reduce((sum, pl) => {
        if (pl.action === "voided" || pl.action === "held" || pl.action === "works_not_completed")
          return sum;
        return sum + pl.paid_amount;
      }, 0),
    [activeProgressLines]
  );

  const difference = useMemo(
    () => Math.round((totalClaimedAmount - totalPaidAmount) * 100) / 100,
    [totalClaimedAmount, totalPaidAmount]
  );

  // Count actions
  const actionCounts = useMemo(() => {
    const counts: Record<string, number> = { pending: 0, paid: 0, voided: 0, held: 0, agreement_made: 0, works_not_completed: 0 };
    for (const pl of activeProgressLines) {
      counts[pl.action] = (counts[pl.action] || 0) + 1;
    }
    return counts;
  }, [activeProgressLines]);
  const paymentState = useMemo(
    () => stableStringify({ datePaid, progressLines }),
    [datePaid, progressLines]
  );
  const initialPaymentState = useRef(paymentState);
  const isDirty = paymentState !== initialPaymentState.current;


  // ─── Handlers ─────────────────────────────────────────────
  const updateProgressLine = useCallback(
    (idx: number, updates: Partial<ProgressLineItem>) => {
      setProgressLines((prev) =>
        prev.map((line, i) => {
          if (i !== idx) return line;
          const updated = { ...line, ...updates };
          if (updates.action === "paid") {
            // Auto-fill 100% only on fresh selection (no user-entered value yet).
            // If user already typed a custom amount, preserve it (allows overpayment).
            if (line.paid_amount === 0 || line.action === "voided" || line.action === "held" || line.action === "works_not_completed" || line.action === "pending") {
              updated.paid_percentage = line.claimed_percentage;
              updated.paid_amount = line.claimed_amount;
            }
          } else if (updates.action === "voided" || updates.action === "held" || updates.action === "works_not_completed") {
            updated.paid_percentage = 0;
            updated.paid_amount = 0;
          } else if (updates.action === "agreement_made") {
            // Keep current paid values, user edits Amount Paid directly.
          }
          // For "pending", keep whatever user typed
          return updated;
        })
      );
    },
    []
  );

  // Set all items to a specific action
  const setAllAction = useCallback(
    (action: PaymentAction) => {
      setProgressLines((prev) =>
        prev.map((line) => {
          if (line.claimed_percentage <= 0) return line;
          const updated = { ...line, action };
          if (action === "voided" || action === "held" || action === "works_not_completed") {
            updated.paid_percentage = 0;
            updated.paid_amount = 0;
          } else if (action === "paid") {
            // Auto-fill 100% only when no user-entered value (allows overpayment)
            if (line.paid_amount === 0 || line.action === "voided" || line.action === "held" || line.action === "works_not_completed" || line.action === "pending") {
              updated.paid_percentage = line.claimed_percentage;
              updated.paid_amount = line.claimed_amount;
            }
          }
          return updated;
        })
      );
    },
    []
  );

  // ─── Reset ────────────────────────────────────────────────
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const executeReset = async () => {
    setShowResetConfirm(false);
    setSaving(true);
    try {
      const mergedLineItems = mergePaymentLineItemsByType(
        existingLineItems,
        "progress",
        []
      );
      const resetPayload = buildPaymentReconciliationInput({
        report,
        lineItems: mergedLineItems,
        datePaid: report.date_paid,
        daysBeforeDue: report.days_before_due,
        changesSummary: `Progress payment reset for Claim #${report.claim_number} (${period}).`,
      });

      const { success, error: resetError } = await savePaymentReconciliation(resetPayload);
      if (!success) throw new Error(resetError);

      addToast({
        type: "success",
        title: "Payment Reset",
        message: "All progress payment data has been cleared.",
      });

      // Close panel and refresh parent data so next open gets clean state
      onSaved();
    } catch (err) {
      logError("Error resetting payment:", err);
      addToast({
        type: "error",
        title: "Reset Failed",
        message: err instanceof Error ? err.message : "Could not reset payment",
      });
    } finally {
      setSaving(false);
    }
  };

  // ─── Save ─────────────────────────────────────────────────
  const handleSave = async () => {
    if (!isDirty) {
      addToast({ type: "info", title: "No changes to save" });
      return;
    }



    setSaving(true);
    try {
      // Build progress line items from subcategory breakdown
      const lineItems: PaymentLineItem[] = [];
      for (const pl of progressLines) {
        // Skip items with no claim or no decision made yet
        if (pl.action === "pending") continue;
        if (pl.claimed_percentage <= 0 && pl.action === "paid") continue;

        // For "paid" action: if user didn't manually enter an amount, auto-fill = claimed
        const paidPct = pl.action === "paid" && pl.paid_percentage === 0
          ? pl.claimed_percentage : pl.paid_percentage;
        const paidAmt = pl.action === "paid" && pl.paid_amount === 0
          ? pl.claimed_amount : pl.paid_amount;

        lineItems.push({
          report_id: report.id,
          line_type: "progress",
          subcategory_id: pl.subcategory_id,
          claimed_percentage: pl.claimed_percentage,
          claimed_amount: pl.claimed_amount,
          paid_percentage: pl.action === "voided" || pl.action === "held" || pl.action === "works_not_completed" ? 0 : paidPct,
          paid_amount: pl.action === "voided" || pl.action === "held" || pl.action === "works_not_completed" ? 0 : paidAmt,
          action: pl.action,
          agreed_amount: pl.action === "agreement_made" ? pl.paid_amount : null,
          notes: pl.notes || null,
          pushed_to_month:
            pl.action === "works_not_completed"
              ? getNextMonth(report.claim_month, report.claim_year).month
              : null,
          pushed_to_year:
            pl.action === "works_not_completed"
              ? getNextMonth(report.claim_month, report.claim_year).year
              : null,
        });
      }

      const mergedLineItems = mergePaymentLineItemsByType(
        existingLineItems,
        "progress",
        lineItems
      );
      const daysBeforeDue = calculateDaysBeforeDue(datePaid, report.payment_due_date);
      const savePayload = buildPaymentReconciliationInput({
        report,
        lineItems: mergedLineItems,
        datePaid,
        daysBeforeDue,
        changesSummary: `Progress payment for Claim #${report.claim_number} (${period}): ${formatCurrency(totalPaidAmount)} paid of ${formatCurrency(totalClaimedAmount)} claimed.`,
      });

      const { success, error: saveError } = await savePaymentReconciliation(savePayload);
      if (!success) throw new Error(saveError);

      // 2. Handle "Works Not Completed" push
      for (const pl of progressLines) {
        if (pl.action === "works_not_completed" && pl.claimed_percentage > 0) {
          await pushToNextMonth(
            pl.subcategory_id,
            pl.claimed_percentage,
            pl.claimed_amount,
            report.claim_month,
            report.claim_year
          );
        }
      }

      // 3. Activity logging
      const actionSummaries: string[] = [];
      for (const pl of progressLines) {
        if (pl.action !== "paid" && pl.claimed_percentage > 0) {
          const label =
            pl.action === "voided" ? "Voided"
              : pl.action === "agreement_made" ? "Agreement Made"
                : "Works Not Completed";
          actionSummaries.push(`${pl.category_name} > ${pl.subcategory_name}: ${label}`);
        }
      }

      await logActivity({
        projectId: report.project_id,
        actionType: "UPDATE",
        entityType: "PAYMENT",
        entityId: report.id,
        description: actionSummaries.length > 0
          ? `Progress payment for Claim #${report.claim_number} (${period}): ${formatCurrency(totalPaidAmount)} paid of ${formatCurrency(totalClaimedAmount)} claimed. Actions: ${actionSummaries.join("; ")}`
          : `Progress payment updated for Claim #${report.claim_number} (${period}): ${formatCurrency(totalPaidAmount)} paid of ${formatCurrency(totalClaimedAmount)} claimed`,
        metadata: {
          claim_number: report.claim_number,
          panel: "progress",
          total_claimed: totalClaimedAmount,
          total_paid: totalPaidAmount,
          date_paid: datePaid,
        },
      });

      addToast({
        type: "success",
        title: "Progress Payment Saved",
        message: `${formatCurrency(totalPaidAmount)} of ${formatCurrency(totalClaimedAmount)} claimed`,
      });
      onSaved();
    } catch (err) {
      logError("Error saving progress payment:", err);
      addToast({
        type: "error",
        title: "Save Failed",
        message: err instanceof Error ? err.message : "Could not save progress payment",
      });
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 20,
          width: "100%",
          maxWidth: 580,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(22,163,74,0.06)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "linear-gradient(135deg, #22c55e, #16a34a)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(22,163,74,0.3)",
              }}
            >
              <TrendingUp size={18} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: "1rem", color: "#0f172a" }}>
                Progress Payment
              </div>
              <div style={{ fontSize: "0.72rem", color: "#64748b" }}>
                Claim #{report.claim_number} • {period}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#64748b",
              padding: 6,
              borderRadius: 8,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable Body */}
        <div style={{ overflow: "auto", flex: 1, padding: "20px 24px" }}>
          {/* ── Summary Cards (read-only, calculated from subcategories) ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 10,
              marginBottom: 20,
            }}
          >
            {/* Claimed */}
            <div
              style={{
                padding: "12px 14px",
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: 12,
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "0.62rem", fontWeight: 600, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>
                Claimed
              </div>
              <div data-seq-private="" style={{ fontSize: "1.05rem", fontWeight: 800, color: "#15803d" }}>
                {formatCurrency(totalClaimedAmount, true)}
              </div>
            </div>
            {/* Paid */}
            <div
              style={{
                padding: "12px 14px",
                background: totalPaidAmount >= totalClaimedAmount && totalClaimedAmount > 0 ? "#f0fdf4" : "#eff6ff",
                border: `1px solid ${totalPaidAmount >= totalClaimedAmount && totalClaimedAmount > 0 ? "#bbf7d0" : "#bfdbfe"}`,
                borderRadius: 12,
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "0.62rem", fontWeight: 600, color: totalPaidAmount >= totalClaimedAmount && totalClaimedAmount > 0 ? "#16a34a" : "#2563eb", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>
                Paid
              </div>
              <div data-seq-private="" style={{ fontSize: "1.05rem", fontWeight: 800, color: totalPaidAmount >= totalClaimedAmount && totalClaimedAmount > 0 ? "#15803d" : "#1d4ed8" }}>
                {formatCurrency(totalPaidAmount, true)}
              </div>
            </div>
            {/* Difference */}
            <div
              style={{
                padding: "12px 14px",
                background: difference > 0 ? "#fffbeb" : "#f0fdf4",
                border: `1px solid ${difference > 0 ? "#fde68a" : "#bbf7d0"}`,
                borderRadius: 12,
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "0.62rem", fontWeight: 600, color: difference > 0 ? "#d97706" : "#16a34a", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>
                Remaining
              </div>
              <div data-seq-private="" style={{ fontSize: "1.05rem", fontWeight: 800, color: difference > 0 ? "#b45309" : "#15803d" }}>
                {formatCurrency(difference, true)}
              </div>
            </div>
          </div>

          {/* ── Legacy Data Banner ── */}
          {hasLegacyData && activeProgressLines.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                padding: "10px 12px",
                borderRadius: 10,
                background: "rgba(59,130,246,0.06)",
                border: "1px solid rgba(59,130,246,0.15)",
                marginBottom: 14,
                fontSize: "0.72rem",
                color: "#2563eb",
              }}
            >
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <strong>Previously recorded:</strong> {legacyPaidPct}% paid ({formatCurrency(legacyPaidAmount)}).
                This has been distributed across subcategories below.
                <span style={{ color: "#64748b" }}> Review and adjust as needed, then save to confirm.</span>
              </div>
            </div>
          )}

          {/* ── Quick Actions ── */}
          {activeProgressLines.length > 1 && (
            <div
              data-tour-id="pp-batch-actions"
              style={{
                display: "flex",
                gap: 6,
                marginBottom: 16,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "0.68rem", fontWeight: 600, color: "#94a3b8", lineHeight: "24px" }}>
                Set all:
              </span>
              <button
                type="button"
                onClick={() => setAllAction("paid")}
                style={{
                  padding: "3px 10px",
                  borderRadius: 6,
                  border: "1px solid #bbf7d0",
                  background: "#f0fdf4",
                  color: "#16a34a",
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <CheckCircle2 size={11} /> All Paid
              </button>
              <button
                type="button"
                onClick={() => setAllAction("voided")}
                style={{
                  padding: "3px 10px",
                  borderRadius: 6,
                  border: "1px solid #fecaca",
                  background: "#fef2f2",
                  color: "#dc2626",
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <AlertTriangle size={11} /> All Void
              </button>
              <div style={{ flex: 1 }} />
              <button
                type="button"
                data-tour-id="pp-reset-button"
                onClick={() => setShowResetConfirm(true)}
                disabled={saving}
                style={{
                  padding: "3px 10px",
                  borderRadius: 6,
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  color: "#64748b",
                  fontSize: "0.68rem",
                  fontWeight: 500,
                  cursor: saving ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  opacity: saving ? 0.5 : 1,
                }}
              >
                <RotateCcw size={10} /> Reset
              </button>
            </div>
          )}

          {/* ── Progress Claim Breakdown (PRIMARY INPUT) ── */}
          {activeProgressLines.length > 0 ? (
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 10,
                }}
              >
                <TrendingUp size={13} color="#16a34a" />
                <span
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#1e293b",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  Progress Claim Breakdown
                </span>
                <span
                  style={{
                    fontSize: "0.62rem",
                    fontWeight: 500,
                    color: "#64748b",
                    background: "#f1f5f9",
                    padding: "1px 6px",
                    borderRadius: 4,
                  }}
                >
                  {activeProgressLines.length} items
                </span>
                {actionCounts.voided > 0 && (
                  <span style={{ fontSize: "0.6rem", fontWeight: 600, color: "#dc2626", background: "#fef2f2", padding: "1px 5px", borderRadius: 4 }}>
                    {actionCounts.voided} voided
                  </span>
                )}
                {actionCounts.works_not_completed > 0 && (
                  <span style={{ fontSize: "0.6rem", fontWeight: 600, color: "#ea580c", background: "#fff7ed", padding: "1px 5px", borderRadius: 4 }}>
                    {actionCounts.works_not_completed} pushed
                  </span>
                )}
              </div>
              <div
                data-tour-id="pp-subcategory-table"
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                {/* Table header */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 80px 100px minmax(110px, auto)",
                    gap: 4,
                    padding: "8px 12px",
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  <span>Category / Subcategory</span>
                  <span style={{ textAlign: "right" }}>Claimed</span>
                  <span style={{ textAlign: "right" }}>Amount Paid</span>
                  <span style={{ textAlign: "right" }}>Action</span>
                </div>

                {/* Line items */}
                {activeProgressLines.map((pl, idx) => {
                  const actualIdx = progressLines.indexOf(pl);
                  const actionColors = getActionColor(pl.action);
                  // Paid & agreement_made allow editing paid values; voided/push/pending are locked
                  const isDisabled = pl.action === "voided" || pl.action === "held" || pl.action === "works_not_completed" || pl.action === "pending";
                  const isZeroed = pl.action === "voided" || pl.action === "held" || pl.action === "works_not_completed" || pl.action === "pending";
                  return (
                    <div
                      key={pl.subcategory_id}
                      {...(idx === 0 ? { 'data-tour-id': 'pp-paid-fields' } : {})}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 80px 100px minmax(110px, auto)",
                        gap: 4,
                        padding: "8px 12px",
                        borderBottom:
                          idx < activeProgressLines.length - 1
                            ? "1px solid #f1f5f9"
                            : "none",
                        fontSize: "0.75rem",
                        alignItems: "center",
                        background: pl.action !== "paid" && pl.action !== "pending" ? actionColors.bg : "transparent",
                        transition: "background 0.15s",
                      }}
                    >
                      {/* Subcategory name */}
                      <div>
                        <div
                          style={{
                            fontWeight: 600,
                            color: "#0f172a",
                            fontSize: "0.72rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 140,
                          }}
                          title={`${pl.category_name} > ${pl.subcategory_name}`}
                        >
                          {pl.subcategory_name}
                        </div>
                        <div style={{ fontSize: "0.6rem", color: "#94a3b8" }}>
                          {pl.category_name}
                        </div>
                      </div>

                      {/* Claimed % + $ */}
                      <div style={{ textAlign: "right", fontWeight: 500, color: "#64748b" }}>
                        {pl.claimed_percentage}%
                        <div data-seq-private="" style={{ fontSize: "0.58rem", color: "#94a3b8" }}>
                          {formatCurrency(pl.claimed_amount)}
                        </div>
                      </div>

                      {/* Amount paid: EDITABLE (percentage derived for grid/history) */}
                      <div style={{ textAlign: "right" }}>
                        <input data-seq-private
                          type="number"
                          value={isZeroed ? 0 : pl.paid_amount}
                          disabled={isDisabled}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const amt = raw === "" ? 0 : parseFloat(raw);
                            if (isNaN(amt)) return;
                            const pct = pl.budget > 0
                              ? Math.round((amt / pl.budget) * 100 * 100) / 100
                              : 0;
                            updateProgressLine(actualIdx, {
                              paid_amount: amt,
                              paid_percentage: pct,
                            });
                          }}
                          style={{
                            width: "100%",
                            padding: "4px 4px",
                            border: `1px solid ${isDisabled ? "#e2e8f0" : "#d1d5db"}`,
                            borderRadius: 5,
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            textAlign: "right",
                            outline: "none",
                            background: isDisabled ? "#f1f5f9" : "#fff",
                            color: isDisabled ? "#94a3b8" : "#0f172a",
                            boxSizing: "border-box",
                          }}
                          step="0.01"
                          min="0"
                        />
                      </div>

                      {/* Action dropdown */}
                      <div style={{ display: "flex", justifyContent: "flex-end" }} {...(idx === 0 ? { 'data-tour-id': 'pp-action-dropdown' } : {})}>
                        <ActionDropdown
                          value={pl.action}
                          onChange={(action) => updateProgressLine(actualIdx, { action })}
                        />
                      </div>

                      {/* Works Not Completed: push info */}
                      {pl.action === "works_not_completed" && (
                        <div
                          style={{
                            gridColumn: "1 / -1",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            marginTop: 4,
                            padding: "5px 8px",
                            background: "#fff7ed",
                            borderRadius: 8,
                            fontSize: "0.68rem",
                            color: "#ea580c",
                          }}
                        >
                          <ArrowRight size={12} />
                          Will push {pl.claimed_percentage}% (
                          {formatCurrency(pl.claimed_amount)}) to{" "}
                          <strong>{nextPeriodLabel}</strong>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* No progress claims, show info message */
            <div
              style={{
                padding: "24px",
                textAlign: "center",
                background: "#f8fafc",
                borderRadius: 12,
                border: "1px solid #e2e8f0",
                marginBottom: 20,
              }}
            >
              <TrendingUp size={24} color="#94a3b8" style={{ marginBottom: 8 }} />
              <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#64748b" }}>
                No Progress Claims
              </div>
              <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginTop: 4 }}>
                This period has no progress claims to reconcile.
                {claimedToDate === 0 && " The Claimed column is $0."}
              </div>
            </div>
          )}

          {/* Date Paid */}
          <div style={{ marginBottom: 8 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 6,
              }}
            >
              <Calendar size={13} color="#64748b" />
              <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b" }}>
                Date Paid
              </span>
            </div>
            <input
              type="date"
              value={datePaid}
              onChange={(e) => setDatePaid(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                fontSize: "0.82rem",
                color: "#0f172a",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
        </div>

        {/* Dashpivot Sync Note */}
        {/* Footer */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Summary */}
          <div style={{ fontSize: "0.72rem", color: "#64748b" }}>
            <strong data-seq-private="" style={{ color: "#0f172a" }}>{formatCurrency(totalPaidAmount, true)}</strong> of{" "}
            <strong data-seq-private="">{formatCurrency(totalClaimedAmount, true)}</strong> claimed
            {difference > 0 && (
              <span style={{ color: "#d97706" }}>
                {" "}• {formatCurrency(difference, true)} remaining
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={onClose}
              style={{
                padding: "8px 16px",
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
                color: "#64748b",
                fontSize: "0.82rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              data-tour-id="pp-save-button"
              onClick={handleSave}
              disabled={saving || !isDirty}
              style={{
                padding: "8px 20px",
                borderRadius: 10,
                border: "none",
                background: saving || !isDirty
                  ? "#94a3b8"
                  : "linear-gradient(135deg, #22c55e, #16a34a)",
                color: "#fff",
                fontSize: "0.82rem",
                fontWeight: 600,
                cursor: saving || !isDirty ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                boxShadow: saving ? "none" : "0 2px 8px rgba(22,163,74,0.3)",
              }}
            >
              {saving && <Loader2 size={14} className="spin" />}
              {saving
                ? "Saving..."
                : !isDirty
                  ? "No Changes"
                  : "Save Progress"}
            </button>
          </div>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      <VaultConfirmModal
        isOpen={showResetConfirm}
        title="Reset Payment Data"
        message="This will delete all saved payment line items for this claim. You can re-enter payment data after resetting."
        confirmLabel="Reset"
        variant="warning"
        onConfirm={executeReset}
        onCancel={() => setShowResetConfirm(false)}
      />

      {/* ── Onboarding Tour ── */}
      {tour.activeTour && (
        <TourEngine
          tour={tour.activeTour}
          onComplete={tour.complete}
          onSkip={tour.skip}
        />
      )}
    </div>
  );
}
